import React from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Building2, BookOpenCheck } from "lucide-react";
import { DEMO_LINE_CONTEXT, practiceShortNameById } from "./mockData";
import {
  entriesForScope,
  KnowledgeBankSnapshot,
  KnowledgeScope,
  useKnowledgeBank,
} from "./knowledgeBankStore";
import { KNOWLEDGE_BANK_PATH } from "./KnowledgeBankPage";

/** Reads "1 answer" / "9 answers". */
const answerCountLabel = (count: number) =>
  `${count} ${count === 1 ? "answer" : "answers"}`;

/** Enough to show the kind of thing the group holds without filling the card. */
const EXAMPLES_SHOWN = 2;

interface AnswerGroupProps {
  bank: KnowledgeBankSnapshot;
  scope: KnowledgeScope;
  /** Who these answers are used for, e.g. "All locations" or "Riverside only". */
  label: string;
  slug: string;
}

/**
 * One group of answers, shown as real caller questions. Examples come from the
 * live bank rather than a written-out description, so the card cannot fall out
 * of step with what the assistant actually knows.
 */
function AnswerGroup({ bank, scope, label, slug }: AnswerGroupProps) {
  const entries = entriesForScope(bank, scope);
  const examples = entries.slice(0, EXAMPLES_SHOWN);
  const remaining = entries.length - examples.length;

  return (
    <div data-testid={`service-context-${slug}`}>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Badge
          variant={scope.tier === "ORG" ? "secondary" : "outline"}
          className="text-[10px]"
        >
          {label}
        </Badge>
        <span className="text-xs text-gray-500">
          {answerCountLabel(entries.length)}
        </span>
      </div>
      <ul className="text-xs text-gray-700 space-y-0.5">
        {examples.map((entry) => (
          <li key={entry.id}>“{entry.question}”</li>
        ))}
      </ul>
      {remaining > 0 && (
        <p className="text-xs text-gray-500 mt-0.5">
          …and {remaining} more like these
        </p>
      )}
    </div>
  );
}

/**
 * Answers the one question a clinic asks about the phone assistant: what will
 * it tell people who call? Real caller questions, grouped by who they are used
 * for, with the gap that matters most called out underneath.
 */
function ServiceContextCard() {
  const bank = useKnowledgeBank();

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Building2 className="h-4 w-4 text-gray-500" />
          What the assistant can answer
        </CardTitle>
        <CardDescription className="text-xs">
          {answerCountLabel(bank.entries.length)} your staff wrote. Anything
          else, it takes a message.
        </CardDescription>
      </CardHeader>
      <CardContent className="px-4 space-y-3">
        <AnswerGroup
          bank={bank}
          scope={{ tier: "ORG" }}
          label="All locations"
          slug="org"
        />
        {DEMO_LINE_CONTEXT.perPracticeSummaries.map(({ practiceId }) => (
          <AnswerGroup
            key={practiceId}
            bank={bank}
            scope={{ tier: "PRACTICE", practiceId }}
            label={`${practiceShortNameById(practiceId)} only`}
            slug={practiceId}
          />
        ))}
        <p className="text-xs text-gray-600 pt-1 border-t">
          {DEMO_LINE_CONTEXT.notCoveredNote}
        </p>
        <div>
          <Link to={KNOWLEDGE_BANK_PATH} data-testid="view-knowledge-bank">
            <Button
              size="sm"
              className="h-8 text-xs bg-brand hover:bg-brand-hover text-white"
            >
              <BookOpenCheck className="h-3.5 w-3.5 mr-1.5 shrink-0" />
              Read or edit all {bank.entries.length} answers
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

export default ServiceContextCard;
